/**
 * VerPesquisaPage — leitura da pesquisa e o chat de aprofundamento (F8).
 *
 * Réplica do legal_research_view.html: metadados da pesquisa, o relatório e
 * a conversa sobre ele (com o feedback por resposta, que alimenta a
 * avaliação de qualidade).
 */

import {
  faArrowLeft,
  faBalanceScale,
  faBroom,
  faCalendar,
  faComments,
  faExclamationTriangle,
  faFileWord,
  faGavel,
  faPaperPlane,
  faSpinner,
  faThumbsDown,
  faThumbsUp,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import {
  avaliarResposta,
  documentoPesquisa,
  excluirPesquisa,
  historicoChat,
  limparChat,
  perguntarChat,
} from '../../api/jurisprudencia'
import { useToast } from '../../components/Toast'

function Chat({ researchId }: { researchId: number }) {
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const [pergunta, setPergunta] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['pesquisa-chat', researchId],
    queryFn: () => historicoChat(researchId),
  })

  const perguntar = useMutation({
    mutationFn: () => perguntarChat(researchId, pergunta.trim()),
    onSuccess: () => {
      setPergunta('')
      void clienteQuery.invalidateQueries({ queryKey: ['pesquisa-chat', researchId] })
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const limpar = useMutation({
    mutationFn: () => limparChat(researchId),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['pesquisa-chat', researchId] })
      notificar('success', 'Conversa apagada.')
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const avaliar = useMutation({
    mutationFn: ({ chatId, feedback }: { chatId: number; feedback: 'util' | 'nao_util' }) =>
      avaliarResposta(researchId, chatId, feedback),
    onSuccess: () =>
      void clienteQuery.invalidateQueries({ queryKey: ['pesquisa-chat', researchId] }),
    onError: (e: Error) => notificar('error', e.message),
  })

  const mensagens = data?.mensagens ?? []

  return (
    <section className="mt-6 rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
      <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faComments} className="mr-2 text-violet-600" />
          Aprofundar a pesquisa
        </h2>
        {mensagens.length > 0 && (
          <button
            type="button"
            onClick={() => limpar.mutate()}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300"
          >
            <FontAwesomeIcon icon={faBroom} />
            Limpar conversa
          </button>
        )}
      </div>

      <div className="space-y-4 p-6">
        {isLoading && (
          <p className="text-sm text-slate-500">
            <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            Carregando conversa…
          </p>
        )}
        {!isLoading && mensagens.length === 0 && (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Pergunte algo sobre o resultado — a resposta usa apenas o conteúdo
            desta pesquisa.
          </p>
        )}
        {mensagens.map((m) => (
          <div key={m.id} className="space-y-2">
            <p className="ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-violet-600 px-4 py-2 text-sm text-white">
              {m.pergunta}
            </p>
            <div className="max-w-[95%] rounded-2xl rounded-bl-sm bg-slate-100 px-4 py-3 dark:bg-slate-800">
              <div
                className="conteudo-documento text-sm text-slate-700 dark:text-slate-300"
                dangerouslySetInnerHTML={{ __html: m.resposta_html }}
              />
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-slate-500">Esta resposta ajudou?</span>
                {(['util', 'nao_util'] as const).map((valor) => (
                  <button
                    key={valor}
                    type="button"
                    onClick={() => avaliar.mutate({ chatId: m.id, feedback: valor })}
                    aria-label={valor === 'util' ? 'Resposta útil' : 'Resposta não útil'}
                    className={`rounded p-1 transition-colors ${
                      m.feedback === valor
                        ? 'text-violet-600'
                        : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    <FontAwesomeIcon icon={valor === 'util' ? faThumbsUp : faThumbsDown} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <form
        className="flex gap-2 border-t border-slate-200 p-4 dark:border-slate-700"
        onSubmit={(e) => {
          e.preventDefault()
          if (!pergunta.trim()) return
          perguntar.mutate()
        }}
      >
        <label htmlFor="pergunta" className="sr-only">
          Pergunta sobre a pesquisa
        </label>
        <input
          id="pergunta"
          name="pergunta"
          type="text"
          value={pergunta}
          onChange={(e) => setPergunta(e.target.value)}
          placeholder="Pergunte sobre o resultado desta pesquisa…"
          className="flex-1 rounded-lg border border-slate-300 px-4 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-violet-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100"
        />
        <button
          type="submit"
          disabled={perguntar.isPending || !pergunta.trim()}
          className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 font-medium text-white transition-colors hover:bg-violet-700 disabled:opacity-50"
        >
          <FontAwesomeIcon
            icon={perguntar.isPending ? faSpinner : faPaperPlane}
            spin={perguntar.isPending}
          />
          Enviar
        </button>
      </form>
    </section>
  )
}

export function VerPesquisaPage() {
  const { id } = useParams()
  const idNumerico = Number(id)
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()

  const { data: pesquisa, isLoading, error } = useQuery({
    queryKey: ['pesquisa-documento', idNumerico],
    queryFn: () => documentoPesquisa(idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })

  const excluir = useMutation({
    mutationFn: () => excluirPesquisa(idNumerico),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['pesquisas-juridicas'] })
      notificar('success', 'Pesquisa excluída.')
      navegar('/jurisprudencia')
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando pesquisa…
      </p>
    )
  }
  if (error || !pesquisa) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <FontAwesomeIcon icon={faBalanceScale} className="mb-4 text-5xl text-slate-300" />
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">
          Pesquisa não encontrada
        </h1>
        <p className="mb-6 text-slate-500">
          {(error as Error)?.message ?? 'A pesquisa não existe ou não é sua.'}
        </p>
        <Link
          to="/jurisprudencia"
          className="inline-flex items-center rounded-lg bg-violet-600 px-6 py-3 font-semibold text-white hover:bg-violet-700"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>
    )
  }

  const metadados = Object.entries({
    Fontes: pesquisa.metadados.fontes,
    Resultados: pesquisa.metadados.total_resultados,
    Qualidade: pesquisa.metadados.qualidade,
    Tempo: pesquisa.metadados.tempo_processamento,
    'Palavras-chave': pesquisa.metadados.palavras_chave,
    Complexidade: pesquisa.metadados.complexidade,
  }).filter(([, valor]) => valor)

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-6 text-center">
        <h1 className="mb-2 text-3xl font-bold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faBalanceScale} className="mr-3 text-violet-600" />
          Pesquisa Jurisprudencial
        </h1>
        <p className="mb-4 text-lg text-slate-600 dark:text-slate-400">
          {pesquisa.consulta}
        </p>
        {pesquisa.criado_em && (
          <span className="inline-flex items-center rounded-full bg-violet-100 px-4 py-2 text-sm font-medium text-violet-800 dark:bg-violet-900/40 dark:text-violet-300">
            <FontAwesomeIcon icon={faCalendar} className="mr-2" />
            {pesquisa.criado_em}
          </span>
        )}
      </header>

      <div className="mb-6 flex flex-col justify-center gap-3 sm:flex-row">
        <a
          href={`/legal_research/${pesquisa.id}/export/docx`}
          className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-emerald-700"
        >
          <FontAwesomeIcon icon={faFileWord} className="mr-2" />
          Exportar DOCX
        </a>
        <button
          type="button"
          onClick={() => excluir.mutate()}
          disabled={excluir.isPending}
          className="inline-flex items-center justify-center rounded-lg bg-red-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-red-700 disabled:opacity-60"
        >
          <FontAwesomeIcon
            icon={excluir.isPending ? faSpinner : faTrashAlt}
            spin={excluir.isPending}
            className="mr-2"
          />
          Excluir
        </button>
        <Link
          to="/jurisprudencia"
          className="inline-flex items-center justify-center rounded-lg bg-slate-600 px-6 py-3 font-semibold text-white shadow-sm transition-all hover:bg-slate-700"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar
        </Link>
      </div>

      {pesquisa.erro && (
        <div className="mb-6 rounded-r-xl border-l-4 border-red-400 bg-red-50 p-4 dark:border-red-600 dark:bg-red-900/20">
          <p className="text-red-800 dark:text-red-300">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            {pesquisa.erro}
          </p>
        </div>
      )}

      {metadados.length > 0 && (
        <dl className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3">
          {metadados.map(([rotulo, valor]) => (
            <div
              key={rotulo}
              className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-dark-card"
            >
              <dt className="text-xs uppercase text-slate-500">{rotulo}</dt>
              <dd className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {valor}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className="bg-gradient-to-r from-violet-600 to-indigo-600 p-6">
          <h2 className="flex items-center text-xl font-bold text-white">
            <FontAwesomeIcon icon={faBalanceScale} className="mr-3" />
            Análise jurisprudencial
          </h2>
        </div>
        <div className="p-8">
          {pesquisa.html ? (
            <div
              className="conteudo-documento leading-relaxed text-slate-700 dark:text-slate-300"
              dangerouslySetInnerHTML={{ __html: pesquisa.html }}
            />
          ) : (
            <p className="py-8 text-center text-slate-500">
              A pesquisa ainda não tem resultado.
            </p>
          )}
        </div>
      </article>

      {(
        [
          [
            'Consultas à base — Lei 14.133/2021',
            pesquisa.referencias_lei,
            'indigo',
          ],
          [
            'Consultas à base local — decretos e jurisprudência',
            pesquisa.referencias_diversas,
            'violet',
          ],
        ] as const
      ).map(([titulo, referencias, cor]) =>
        referencias.length ? (
          <section
            key={titulo}
            className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card"
          >
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
              <FontAwesomeIcon
                icon={cor === 'indigo' ? faBalanceScale : faGavel}
                className={
                  cor === 'indigo' ? 'text-indigo-600' : 'text-violet-600'
                }
              />
              {titulo}
            </h2>
            <ol className="space-y-3">
              {referencias.map((r, i) => (
                <li
                  key={i}
                  className={`rounded-lg border-l-4 bg-slate-50 p-4 dark:bg-slate-800 ${
                    cor === 'indigo' ? 'border-indigo-500' : 'border-violet-500'
                  }`}
                >
                  <p className="font-semibold text-slate-800 dark:text-slate-100">
                    {i + 1}. {r.titulo}
                  </p>
                  {r.trecho && (
                    <p className="mt-1 text-sm italic text-slate-600 dark:text-slate-400">
                      “{r.trecho}”
                    </p>
                  )}
                </li>
              ))}
            </ol>
          </section>
        ) : null,
      )}

      <Chat researchId={idNumerico} />
    </div>
  )
}
